import type { Article } from "@/features/knowledge-bank/types";

// UTKAST — granska sakuppgifterna och justera tonen innan status sätts till
// "published". Texten bygger på hur köerna fungerar på CampusLyan (se
// src/data/faq.ts) och allmänt om studentbostadsköer i Sverige.
export const hurFungerarBostadskoer: Article = {
  slug: "hur-fungerar-bostadskoer",
  status: "draft",
  publishedAt: "2026-09-18",
  content: {
    sv: {
      title: "Så fungerar bostadsköer för studenter",
      description:
        "Kötid, köpoäng och Express-bostäder: så fungerar bostadsköerna för studentbostäder, och så ökar du dina chanser att få en bostad i tid.",
      intro: [
        "De flesta studentbostäder i Sverige fördelas via bostadsköer. Det betyder att det sällan räcker att hitta en ledig lägenhet och ansöka. Det som avgör är hur länge du har stått i kön hos just det bostadsföretaget. Den här guiden går igenom hur köerna fungerar, vad som skiljer olika bostadsföretag åt och hur du planerar för att ha ett boende klart när terminen börjar.",
      ],
      sections: [
        {
          heading: "Vad är en bostadskö?",
          body: [
            "En bostadskö är bostadsföretagets sätt att fördela lediga bostäder rättvist. Varje person i kön samlar kötid, oftast räknat i dagar från den dag man ställde sig i kön. När en bostad blir ledig får de sökande med längst kötid, som samtidigt uppfyller kraven för bostaden, erbjudandet först.",
            "Kötiden räknas separat hos varje bostadsföretag. Står du i tre köer har du alltså tre olika kötider, och de påverkar inte varandra.",
          ],
        },
        {
          heading: "När ska jag ställa mig i kö?",
          body: [
            "Så tidigt som möjligt. Kötid är det enda i processen som du inte kan skynda på i efterhand, och i de stora studentstäderna kan det krävas lång kötid för att få ett erbjudande under terminsstarten, när efterfrågan är som störst.",
            "Du behöver inget antagningsbesked för att stå i kö. Många ställer sig i köerna redan under gymnasiet eller när de börjar fundera på vilka utbildningar de ska söka. Vissa bostadsföretag kräver däremot studieintyg eller antagningsbesked när du väl tackar ja till en bostad, så läs kraven för varje bostad.",
          ],
        },
        {
          heading: "Ställ dig i flera köer",
          body: [
            "Eftersom du sällan vet i förväg var du blir antagen lönar det sig att stå i kö i flera städer och hos flera bostadsföretag samtidigt. Det kostar inget på CampusLyan, och du kan följa kötiden i alla köer från samma konto.",
            { tip: "Markera städerna och lärosätena du är intresserad av under Mina intressen. Det begränsar ingenting, men gör det lättare att hålla koll på rätt köer." },
          ],
        },
        {
          heading: "Express: bostäder utan kö",
          body: [
            "Alla bostäder kräver inte kötid. På CampusLyan är vissa bostäder märkta Express. De går till den som ansöker först, och bostadsföretaget hanterar ansökningarna i den ordning de kommer in.",
            "Express-bostäder är ett bra alternativ om du behöver flytta snart, om du har blivit antagen sent eller om du ännu inte har hunnit samla kötid. Håll utkik efter dem i bostadssökningen, där de är märkta med en gul Express-symbol.",
          ],
        },
        {
          heading: "Vad händer om jag lämnar en kö?",
          body: [
            "Lämnar du en kö försvinner din plats och all kötid du har samlat i den kön direkt, och den går inte att få tillbaka. Går du med igen börjar du om från noll. Tänk därför efter innan du lämnar en kö du har stått i länge, även om du för tillfället inte söker något i den staden.",
            "Har du redan kötid hos ett bostadsföretag från deras eget kösystem följer den med när du går med via CampusLyan. Du börjar alltså inte om bara för att du hittade företaget här.",
          ],
        },
        {
          heading: "Checklista inför terminsstarten",
          body: [
            {
              list: [
                "Skapa ett konto och verifiera din identitet, så att du kan ställa dig i kö och ansöka.",
                "Ställ dig i kö hos bostadsföretagen i alla städer du kan tänka dig att plugga i.",
                "Läs kravprofilen för bostäderna du är intresserad av och ha studieintyg eller antagningsbesked redo.",
                "Håll koll på Express-bostäder om du behöver flytta snabbt.",
                "Svara i tid när du får ett erbjudande. Missar du svarstiden går bostaden vidare till nästa i kön.",
              ],
            },
          ],
        },
      ],
    },
  },
};
